import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LeadershipMenu from '@/components/LeadershipMenu';
import CampusMenu from '@/components/CampusMenu';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { IITH } from '@/pages/landing/iithBrand';
import { IITH_LOGO_URL } from '@/config/iithAssets';

/**
 * Shared IITH campus chrome (FIC-style): saffron stripe, navy header,
 * horizontal menu. Used by leadership, reception, doctor, pharmacy —
 * not clinic /admin (sidebar).
 */
export default function LeadershipChrome({
  children,
  actions,
  homeHref = '/management',
  roleLabel = 'Leadership',
  menuItems = null,
  showLeadershipMenu = false,
}) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const clinic = tenant?.branding?.display_name || tenant?.name || IITH.hospital;
  const useLeadershipNav = showLeadershipMenu || menuItems == null;

  return (
    <div className="min-h-screen bg-[#E8EEF5]" data-testid="leadership-chrome">
      <div className="h-1" style={{ background: `linear-gradient(90deg, ${IITH.saffron}, ${IITH.gold}, ${IITH.saffron})` }} />
      <header
        className="sticky top-0 z-30 border-b border-white/10 text-white"
        style={{ background: `linear-gradient(110deg, ${IITH.ink}, ${IITH.navy} 55%, ${IITH.navyMid})` }}
      >
        <div className="max-w-[1500px] mx-auto px-4 sm:px-8 h-14 flex items-center gap-3 justify-between">
          <button
            type="button"
            onClick={() => navigate(homeHref)}
            className="flex items-center gap-2.5 min-w-0 text-left hover:opacity-95"
            data-testid="leadership-brand"
          >
            <img src={IITH_LOGO_URL} alt="" className="h-9 w-9 rounded-full bg-white p-0.5 object-contain shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold font-heading truncate leading-tight">{clinic}</p>
              <p className="text-[10px] uppercase tracking-[0.18em] truncate" style={{ color: IITH.gold }}>
                {roleLabel}
              </p>
            </div>
          </button>
          <div className="flex items-center gap-2 shrink-0">
            {actions}
            {user?.name ? (
              <span className="hidden md:inline text-xs text-[#9AADBE] max-w-[200px] truncate">
                {user.name}
              </span>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { void logout(); }}
              className="text-white/90 hover:bg-white/10"
              data-testid="leadership-logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      {useLeadershipNav ? <LeadershipMenu /> : <CampusMenu items={menuItems} />}
      {children}
    </div>
  );
}
